import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Paper,
  Divider,
  Chip,
  useTheme,
  Avatar,
} from '@mui/material';
import { format } from 'date-fns';
import { BookingFunnelStep } from '../../firebase/services/abandonedCartService';
import { CheckCircle, Error, Warning, Info } from '@mui/icons-material';

interface BookingSessionDetailProps {
  session: BookingFunnelStep;
  open: boolean;
  onClose: () => void;
}

const getStepIcon = (step: string, status: string) => {
  if (status === 'completed') return <CheckCircle color="success" />;
  if (status === 'abandoned') return <Error color="error" />;
  if (step.includes('error')) return <Warning color="warning" />;
  return <Info color="info" />;
};

const formatTime = (date: Date | any) => {
  try {
    // Handle Firestore Timestamp
    if (date && typeof date.toDate === 'function') {
      return format(date.toDate(), 'MMM d, yyyy h:mm:ss a');
    }
    // Handle Firestore Timestamp format with seconds and nanoseconds
    if (date && typeof date === 'object' && 'seconds' in date) {
      return format(new Date(date.seconds * 1000), 'MMM d, yyyy h:mm:ss a');
    }
    // Handle regular Date object or timestamp number
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) {
      return 'Invalid Date';
    }
    return format(dateObj, 'MMM d, yyyy h:mm:ss a');
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid Date';
  }
};

const formatValue = (value: any): string => {
  if (value === null || value === undefined) return 'N/A';
  
  // Handle Firestore Timestamp
  if (value && typeof value.toDate === 'function') {
    return formatTime(value);
  }
  
  // Handle Firestore Timestamp format with seconds and nanoseconds
  if (value && typeof value === 'object' && 'seconds' in value) {
    return formatTime(value);
  }
  
  // Handle Date objects
  if (value instanceof Date) {
    return formatTime(value);
  }
  
  // Handle objects (but not Firestore Timestamps)
  if (typeof value === 'object' && !('seconds' in value)) {
    return JSON.stringify(value);
  }
  
  // Handle everything else
  return String(value);
};

const getTimeDiff = (start: Date | any, end: Date | any) => {
  try {
    // Convert Firestore Timestamps to Date objects
    const startDate = start && typeof start.toDate === 'function' ? start.toDate() : new Date(start);
    const endDate = end && typeof end.toDate === 'function' ? end.toDate() : new Date(end);
    
    const diff = endDate.getTime() - startDate.getTime();
    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  } catch (error) {
    console.error('Error calculating time difference:', error);
    return 'N/A';
  }
};

const BookingSessionDetail: React.FC<BookingSessionDetailProps> = ({
  session,
  open,
  onClose,
}) => {
  const theme = useTheme();
  const steps = session.steps || {};
  const stepEntries = Object.entries(steps).sort(
    (a, b) => new Date(a[1].timestamp).getTime() - new Date(b[1].timestamp).getTime()
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Booking Session Details</Typography>
          <Chip
            label={session.status}
            color={
              session.status === 'completed'
                ? 'success'
                : session.status === 'abandoned'
                ? 'error'
                : 'warning'
            }
          />
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box mb={3}>
          <Typography variant="subtitle1" gutterBottom>
            Session Information
          </Typography>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2}>
              <Typography variant="body2">
                <strong>Session ID:</strong> {session.sessionId}
              </Typography>
              <Typography variant="body2">
                <strong>Customer ID:</strong> {session.customerId}
              </Typography>
              <Typography variant="body2">
                <strong>Device:</strong> {session.metadata?.deviceInfo}
              </Typography>
              <Typography variant="body2">
                <strong>Browser:</strong> {session.metadata?.browserInfo}
              </Typography>
            </Box>
          </Paper>
        </Box>

        <Typography variant="subtitle1" gutterBottom>
          Booking Flow Timeline
        </Typography>
        
        {/* Custom Timeline Implementation */}
        <Box sx={{ position: 'relative', pl: 3 }}>
          {stepEntries.map(([step, data], index) => {
            const nextStep = stepEntries[index + 1];
            const timeSpent = nextStep
              ? getTimeDiff(data.timestamp, nextStep[1].timestamp)
              : null;

            return (
              <Box key={step} sx={{ position: 'relative', pb: index < stepEntries.length - 1 ? 3 : 0 }}>
                {/* Timeline dot */}
                <Avatar
                  sx={{
                    position: 'absolute',
                    left: -24,
                    top: 0,
                    width: 40,
                    height: 40,
                    bgcolor: step.includes('error')
                      ? theme.palette.error.main
                      : step === 'confirmation'
                      ? theme.palette.success.main
                      : theme.palette.primary.main,
                  }}
                >
                  {getStepIcon(step, session.status)}
                </Avatar>
                {/* Timeline connector */}
                {index < stepEntries.length - 1 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: -8,
                      top: 40,
                      width: 2,
                      height: 'calc(100% - 16px)',
                      bgcolor: theme.palette.divider,
                    }}
                  />
                )}
                {/* Timeline content */}
                <Box sx={{ ml: 3 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                    <Typography variant="body2" color="textSecondary">
                      {formatTime(data.timestamp)}
                    </Typography>
                    {timeSpent && (
                      <Typography variant="caption" color="textSecondary">
                        Time spent: {timeSpent}
                      </Typography>
                    )}
                  </Box>
                  
                  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {step.replace(/_/g, ' ').toUpperCase()}
                    </Typography>
                    {data.data && (
                      <Box mt={1}>
                        {Object.entries(data.data).map(([key, value]) => (
                          <Typography key={key} variant="body2">
                            <strong>{key}:</strong>{' '}
                            {formatValue(value)}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Paper>
                </Box>
              </Box>
            );
          })}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default BookingSessionDetail; 